import { ArrowRightIcon } from "@talladega/icons";
import { useTranslation } from "next-i18next";
import { tryGetActiveAftermarketPurchase } from "@modules/purchase/warranty/types/Purchase";
import useAftermarketPurchase from "@modules/purchase/warranty/hooks/useAftermarketPurchase";
import {
  aftermarketDetailsStep,
  aftermarketQuoteStep,
  getRemainingPurchaseSteps,
  getStepById,
  showResumeForStep,
  showResumeQuoteStep,
} from "@modules/purchase/PurchaseStep";
import { useRouter } from "next/router";
import useLogResumeClickMutation from "@common/hooks/useLogResumeClickMutation";
import { Button } from "@talladega/components";
import DealCustomerTypeEnum from "@common/enums/DealCustomerTypeEnum";
import { getProvinceById } from "@talladega/types";
import { sources } from "@common/enums/SourceEnum";

type NavBarProps = {
  showResumePurchase?: boolean;
};

const WarrantyResumePurchaseMobileFloater = ({
  showResumePurchase = false,
}: NavBarProps) => {
  const { t } = useTranslation(["common", "purchase"]);

  const router = useRouter();

  const { purchase: purchaseResponse } = useAftermarketPurchase();
  const purchase = tryGetActiveAftermarketPurchase(purchaseResponse);
  const { mutate: logResumeClickMutation } = useLogResumeClickMutation();

  const mainApplicant = purchase?.customers.find(
    (c) => c.typeId === DealCustomerTypeEnum.MainApplicant
  );

  const provinceAbbr = mainApplicant?.currentAddress?.provinceId
    ? getProvinceById(mainApplicant.currentAddress.provinceId)?.abbr
    : "";

  const odometer = purchase?.vehicle?.odometerCount;
  const vin = purchase?.vehicle?.vin;
  const year = purchase?.vehicle?.year;
  const make = purchase?.vehicle?.make;
  const model = purchase?.vehicle?.model;

  const hasValidVehicleInfo = !!vin || (!!year && !!make && !!model);

  const hasProvince = !!provinceAbbr;
  const hasOdometer = !!odometer;

  const showResumeQuoteButton =
    !!purchase &&
    showResumeQuoteStep(purchase.currentStepId) &&
    hasValidVehicleInfo &&
    hasProvince &&
    hasOdometer;

  const showResumeButton =
    !!purchase && showResumeForStep(purchase.currentStepId);

  const purchaseStep = getStepById(
    "aftermarket",
    purchase?.currentStepId ?? aftermarketDetailsStep.id
  );

  const handleOnResumePurchaseClick = () => {
    if (!purchase) return;

    logResumeClickMutation(purchase.id);
    if (purchaseStep) {
      router.push({
        pathname: purchaseStep.href,
      });
    }
  };

  const handleOnResumeQuoteClick = () => {
    if (!purchase) return;

    if (hasValidVehicleInfo && hasProvince && hasOdometer) {
      const quoteParams: Record<string, string> = {
        source:
          sources
            .find((s) => s.name === purchase.dealSource.toString())
            ?.name.toLowerCase() || "",
        odometer: odometer.toString(),
        province: provinceAbbr,
        removeDiscount: "true",
      };

      if (vin) {
        quoteParams.vin = vin;
      } else {
        quoteParams.year = year?.toString() || "";
        quoteParams.make = make || "";
        quoteParams.model = model || "";
        quoteParams.trim = purchase.vehicle?.trim || "";
      }

      const { href } = aftermarketQuoteStep;
      router.push(`${href}?${new URLSearchParams(quoteParams).toString()}`);
    }
  };

  const pathname = router?.pathname;

  const showResume = showResumePurchase && showResumeButton;
  const showResumeQuote =
    showResumePurchase && showResumeQuoteButton && !pathname.includes("/quote");

  const remainingSteps = getRemainingPurchaseSteps("aftermarket", purchaseStep);

  if (!showResume && !showResumeQuote) return null;

  return (
    <div className="lg:hidden bg-secondary-800 fixed bottom-0 inset-x-0 z-50 flex flex-col items-start justify-center p-4 space-y-2">
      <p className="text-white text-left">
        {t("purchase:dont_wait_protect_your_ride")}
      </p>
      {!showResumeQuote && remainingSteps > 0 && (
        <p className="text-white text-left">
          {t("purchase:youre_just_value_quick_steps", {
            value: remainingSteps,
            stepsLabel:
              remainingSteps > 1 ? t("common:steps") : t("common:step"),
          })}
        </p>
      )}

      {showResumeQuote ? (
        <Button
          rightIcon={<ArrowRightIcon />}
          onClick={handleOnResumeQuoteClick}
          buttonStyle="dark-secondary"
          className="w-full py-3"
        >
          {t("purchase:view_my_quote")}
        </Button>
      ) : (
        <Button
          rightIcon={<ArrowRightIcon />}
          onClick={handleOnResumePurchaseClick}
          buttonStyle="dark-secondary"
          className="w-full py-3"
        >
          {t("purchase:resume_purchase")}
        </Button>
      )}
    </div>
  );
};

export default WarrantyResumePurchaseMobileFloater;
